import { isNodeEnvironment } from './is-node';
/**
 * Retrieves the local non-loopback IPv4 address of the host machine.
 * This function is **only reliable and intended for use in a Node.js environment**
 * as it relies on the built-in 'os' module.
 *
 * In a browser environment, it will return a safe, default IP address and log a warning.
 *
 * @public
 * @function
 * @returns {string} The found local IPv4 address (e.g., '192.168.1.100') or
 * a default fallback address ('0.0.0.0' or '127.0.0.1') if no suitable address is found
 * or if the function runs outside of Node.js.
 *
 * @example
 * // 1. Example in a Node.js environment:
 * // (Assuming the host has an IP 192.168.1.100)
 * const ipAddress: string = resolveLocalIPv4Address();
 * console.log(`Local IP: ${ipAddress}`); // Output: Local IP: 192.168.1.100
 *
 * @example
 * // 2. Example in a browser environment:
 * const ipAddress: string = resolveLocalIPv4Address();
 * // A console warning will be logged indicating Node.js dependence.
 * console.log(`Local IP: ${ipAddress}`); // Output: Local IP: 0.0.0.0
 */
export const resolveLocalIPv4Address = () => {
    if (!isNodeEnvironment) {
        // If running outside of Node.js (i.e., in a browser or restricted environment):
        console.warn('resolveLocalIPv4Address: Function designed for Node.js was called in a non-Node environment.');
        return '0.0.0.0';
    }
    // This code block is guaranteed to execute ONLY within a Node.js runtime.
    // Using a conditional 'require' prevents module bundlers (like Webpack or Vite)
    // from erroneously trying to package the Node.js built-in 'os' module for browser use.
    const osModule = require('os');
    // Calls the core 'os.networkInterfaces()' method to retrieve comprehensive details
    // for all network adapters available on the host machine.
    const networkInterfaces = osModule.networkInterfaces();
    // Iterates through all arrays of address details associated with each network interface name.
    // Object.values() extracts the arrays (values) from the networkInterfaces dictionary.
    for (const interfaceAddresses of Object.values(networkInterfaces)) {
        // Iterates over the array of address configurations (IPv4/IPv6, internal/external) for the current network adapter.
        for (const addressDetails of interfaceAddresses ?? []) {
            // 1. Checks if the address family is strictly 'IPv4'.
            // 2. Checks if the address is NOT 'internal' (i.e., it excludes loopback interfaces like 127.0.0.1).
            if (addressDetails.family === 'IPv4' && !addressDetails.internal) {
                return addressDetails.address;
            }
        }
    }
    return '127.0.0.1';
};
//# sourceMappingURL=network.js.map